import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import {
  checkLanguageConfiguration,
  greetingHasPrimaryScript,
  languageName,
} from "@/config/language-rules";
import { rewriteGreeting } from "@/services/greetingRewriteService";
import { microPromptService } from "@/services/microPromptService";
import { MicroPrompt } from "@/types/microPrompt";
import { isRealtimeModel } from "@/utils/realtimeModels";
import { Info, Loader2, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { supabase } from "../../../../supabase/supabase";

export default function PromptTab() {
  const { control, watch, setValue } = useFormContext();
  const [dynamicVariables, setDynamicVariables] = useState<string[]>([]);
  const [microPrompts, setMicroPrompts] = useState<MicroPrompt[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const textareaRefs = {
    systemPrompt: useRef<HTMLTextAreaElement>(null),
    summaryPrompt: useRef<HTMLTextAreaElement>(null),
    evaluationPrompt: useRef<HTMLTextAreaElement>(null),
    structuredDataPrompt: useRef<HTMLTextAreaElement>(null),
    structuredJSONSchema: useRef<HTMLTextAreaElement>(null),
  };

  // Watch all prompt fields to extract dynamic variables
  const firstMessage = watch("firstMessage");
  const systemPrompt = watch("systemPrompt"); // Now maps directly to system_prompt column
  const summaryPrompt = watch("summaryPrompt"); // Now maps directly to summary_prompt column
  const evaluationPrompt = watch("evaluationPrompt"); // Now maps directly to evaluation_prompt column
  const structuredDataPrompt = watch("structuredDataPrompt"); // Now maps directly to structured_data_prompt column
  const structuredJSONSchema = watch("structuredJSONSchema"); // Now maps directly to structured_json_schema column
  const endCallMessage = watch("endCallMessage");

  // Language setting. Realtime models are not part of this feature, so they read as no setting.
  const llmProvider = watch("llm_provider");
  const llmModel = watch("llm_model");
  const languageSetting = isRealtimeModel(llmProvider, llmModel)
    ? null
    : checkLanguageConfiguration(watch("language_configuration"));
  const primaryName = languageSetting ? languageName(languageSetting.primary) : "";
  const greetingNeedsRewrite =
    !!languageSetting &&
    typeof firstMessage === "string" &&
    firstMessage.trim() !== "" &&
    !greetingHasPrimaryScript(firstMessage, languageSetting.primary);
  const [isRewriting, setIsRewriting] = useState(false);
  // The text before the last rewrite; one level of undo.
  const [greetingBeforeRewrite, setGreetingBeforeRewrite] = useState<string | null>(null);
  // The text the rewrite put in; an edit that changes it drops the undo.
  const rewrittenGreeting = useRef<string | null>(null);

  useEffect(() => {
    if (greetingBeforeRewrite !== null && firstMessage !== rewrittenGreeting.current) {
      setGreetingBeforeRewrite(null);
    }
  }, [firstMessage, greetingBeforeRewrite]);

  const handleRewriteGreeting = async () => {
    if (!languageSetting || isRewriting) return;
    const original = firstMessage as string;
    setIsRewriting(true);
    try {
      const rewritten = await rewriteGreeting({
        text: original,
        language: languageSetting.primary,
      });
      rewrittenGreeting.current = rewritten;
      setGreetingBeforeRewrite(original);
      setValue("firstMessage", rewritten, { shouldDirty: true });
    } catch (error: any) {
      toast({
        title: "Could not rewrite the greeting",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRewriting(false);
    }
  };

  const handleUndoRewrite = () => {
    if (greetingBeforeRewrite === null) return;
    setValue("firstMessage", greetingBeforeRewrite, { shouldDirty: true });
    setGreetingBeforeRewrite(null);
  };

  // Get current user's organization ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        const { data: userData } = await supabase
          .from("users")
          .select("organization_id")
          .eq("id", data.user.id)
          .single();

        if (userData?.organization_id) {
          setOrganizationId(userData.organization_id);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Fetch micro prompts when organization ID is available
  useEffect(() => {
    if (organizationId) {
      const fetchMicroPrompts = async () => {
        setIsLoadingPrompts(true);
        try {
          const data = await microPromptService.getMicroPrompts(organizationId);
          setMicroPrompts(data);
        } catch (error) {
          console.error("Error fetching micro prompts:", error);
        } finally {
          setIsLoadingPrompts(false);
        }
      };
      fetchMicroPrompts();
    }
  }, [organizationId]);

  // Extract dynamic variables from text (anything inside curly braces)
  useEffect(() => {
    const allText = [
      firstMessage,
      systemPrompt,
      summaryPrompt,
      evaluationPrompt,
      structuredDataPrompt,
      endCallMessage,
    ]
      .filter(Boolean)
      .join(" ");

    const regex = /\{([^}]+)\}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(allText)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }

    setDynamicVariables(matches);

    // Store the extracted variables in the form data
    setValue("variables", matches);
  }, [
    firstMessage,
    systemPrompt,
    summaryPrompt,
    evaluationPrompt,
    structuredDataPrompt,
    endCallMessage,
    setValue,
  ]);

  // Function to get the source of each variable
  const getVariableSources = (variable: string) => {
    const sources = [];

    if (firstMessage?.includes(`{${variable}}`)) sources.push("First Message");
    if (systemPrompt?.includes(`{${variable}}`)) sources.push("System Prompt");
    if (endCallMessage?.includes(`{${variable}}`))
      sources.push("End Call Message");
    if (summaryPrompt?.includes(`{${variable}}`))
      sources.push("Summary Prompt");
    if (evaluationPrompt?.includes(`{${variable}}`))
      sources.push("Evaluation Prompt");
    if (structuredDataPrompt?.includes(`{${variable}}`))
      sources.push("Structured Data Prompt");
    if (structuredJSONSchema?.includes(`{${variable}}`))
      sources.push("Structured JSON Schema");

    return sources.join(", ");
  };

  // Filter micro prompts based on search query
  const filteredMicroPrompts = microPrompts.filter(
    (prompt) =>
      prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.content.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Insert micro prompt content at cursor position
  const insertMicroPrompt = (fieldName: string, content: string) => {
    // Get the appropriate textarea ref
    const textareaRef = textareaRefs[fieldName as keyof typeof textareaRefs];
    if (!textareaRef?.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Get current value
    const currentValue = watch(fieldName) || "";

    // Create new value with micro prompt inserted at cursor
    const newValue =
      currentValue.substring(0, start) + content + currentValue.substring(end);

    // Update form value
    setValue(fieldName, newValue, { shouldDirty: true, shouldTouch: true });

    // Close popover
    setOpenPopover(null);

    // Focus back on textarea and set cursor position after inserted content
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + content.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardHeader>
        <CardTitle className="text-foreground">Prompt Configuration</CardTitle>
        <CardDescription>
          Configure all prompt-related settings for your assistant.
          {microPrompts.length > 0 && (
            <span className="ml-1">
              Use the <Sparkles className="inline h-3 w-3 text-foreground" />{" "}
              button to insert micro prompts.
            </span>
          )}
        </CardDescription>
        {dynamicVariables.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm font-medium">Dynamic Variables:</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={16} className="text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Variables in curly braces like {"{variable_name}"} will be
                      replaced with lead data when making calls
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex flex-wrap gap-2">
              {dynamicVariables.map((variable) => (
                <TooltipProvider key={variable}>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge
                        variant="outline"
                        className="bg-secondary text-foreground border-border"
                      >
                        {variable}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Used in: {getVariableSources(variable)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="firstMessage"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">First Message</FormLabel>
              <FormControl>
                <Input
                  placeholder="Hello, how can I help you today?"
                  {...field}
                  className="bg-muted text-foreground border-input"
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                The first message the assistant will send to the user.
              </FormDescription>
              {greetingNeedsRewrite && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-amber-600 dark:text-amber-500">
                  <span>
                    This greeting has no {primaryName} in it. The call starts in{" "}
                    {primaryName}.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRewriteGreeting}
                    disabled={isRewriting}
                  >
                    {isRewriting ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1 h-3 w-3" />
                    )}
                    {isRewriting ? "Rewriting..." : `Rewrite in ${primaryName}`}
                  </Button>
                </div>
              )}
              {greetingBeforeRewrite !== null && (
                <div className="text-sm text-muted-foreground">
                  Greeting rewritten. Not saved yet.{" "}
                  <button
                    type="button"
                    className="underline text-foreground"
                    onClick={handleUndoRewrite}
                  >
                    Undo
                  </button>
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="systemPrompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">System Prompt</FormLabel>
              <div className="relative">
                <FormControl>
                  <Textarea
                    placeholder="You are a helpful assistant..."
                    className="min-h-32 bg-muted text-foreground border-input pr-10"
                    {...field}
                    ref={textareaRefs.systemPrompt}
                  />
                </FormControl>
                {microPrompts.length > 0 && (
                  <Popover
                    open={openPopover === "systemPrompt"}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? "systemPrompt" : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 h-6 w-6 text-foreground hover:text-foreground hover:bg-secondary"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <Command>
                        <div className="p-2">
                          <Input
                            placeholder="Search micro prompts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {isLoadingPrompts ? (
                            <div className="flex justify-center p-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            </div>
                          ) : filteredMicroPrompts.length === 0 ? (
                            <p className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery
                                ? "No matching micro prompts found"
                                : "No micro prompts available"}
                            </p>
                          ) : (
                            filteredMicroPrompts.map((prompt) => (
                              <CommandItem
                                key={prompt.id}
                                onSelect={() =>
                                  insertMicroPrompt(
                                    "systemPrompt",
                                    prompt.content,
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {prompt.title}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {prompt.content.length > 50
                                      ? prompt.content.substring(0, 50) + "..."
                                      : prompt.content}
                                  </span>
                                </div>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <FormDescription className="text-muted-foreground">
                The system prompt that defines the assistant's behavior and
                capabilities.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="endCallMessage"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">
                End Call Message
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Goodbye."
                  {...field}
                  className="bg-muted text-foreground border-input"
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Message to say when ending the call.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="summaryPrompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Summary Prompt</FormLabel>
              <div className="relative">
                <FormControl>
                  <Textarea
                    placeholder="You are an expert note-taker. You will be given a transcript of a call. Summarize the call in 2-3 sentences, if applicable."
                    className="min-h-32 bg-muted text-foreground border-input pr-10"
                    {...field}
                    ref={textareaRefs.summaryPrompt}
                  />
                </FormControl>
                {microPrompts.length > 0 && (
                  <Popover
                    open={openPopover === "summaryPrompt"}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? "summaryPrompt" : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 h-6 w-6 text-foreground hover:text-foreground hover:bg-secondary"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <Command>
                        <div className="p-2">
                          <Input
                            placeholder="Search micro prompts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {isLoadingPrompts ? (
                            <div className="flex justify-center p-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            </div>
                          ) : filteredMicroPrompts.length === 0 ? (
                            <p className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery
                                ? "No matching micro prompts found"
                                : "No micro prompts available"}
                            </p>
                          ) : (
                            filteredMicroPrompts.map((prompt) => (
                              <CommandItem
                                key={prompt.id}
                                onSelect={() =>
                                  insertMicroPrompt(
                                    "summaryPrompt",
                                    prompt.content,
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {prompt.title}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {prompt.content.length > 50
                                      ? prompt.content.substring(0, 50) + "..."
                                      : prompt.content}
                                  </span>
                                </div>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <FormDescription className="text-muted-foreground">
                Prompt used to generate a summary of the conversation.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="evaluationPrompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">
                Evaluation Prompt
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Textarea
                    placeholder="You are an expert call evaluator. You will be given a transcript of a call and the system prompt of the AI participant. Determine if the call was successful based on the objectives inferred from the system prompt."
                    className="min-h-32 bg-muted text-foreground border-input pr-10"
                    {...field}
                    ref={textareaRefs.evaluationPrompt}
                  />
                </FormControl>
                {microPrompts.length > 0 && (
                  <Popover
                    open={openPopover === "evaluationPrompt"}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? "evaluationPrompt" : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 h-6 w-6 text-foreground hover:text-foreground hover:bg-secondary"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <Command>
                        <div className="p-2">
                          <Input
                            placeholder="Search micro prompts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {isLoadingPrompts ? (
                            <div className="flex justify-center p-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            </div>
                          ) : filteredMicroPrompts.length === 0 ? (
                            <p className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery
                                ? "No matching micro prompts found"
                                : "No micro prompts available"}
                            </p>
                          ) : (
                            filteredMicroPrompts.map((prompt) => (
                              <CommandItem
                                key={prompt.id}
                                onSelect={() =>
                                  insertMicroPrompt(
                                    "evaluationPrompt",
                                    prompt.content,
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {prompt.title}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {prompt.content.length > 50
                                      ? prompt.content.substring(0, 50) + "..."
                                      : prompt.content}
                                  </span>
                                </div>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <FormDescription className="text-muted-foreground">
                Prompt used to evaluate the success of the conversation.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="structuredDataPrompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">
                Structured Data Prompt
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Textarea
                    placeholder="You will be given a transcript of a call and the system prompt of the AI participant. Extract..."
                    className="min-h-32 bg-muted text-foreground border-input pr-10"
                    {...field}
                    ref={textareaRefs.structuredDataPrompt}
                  />
                </FormControl>
                {microPrompts.length > 0 && (
                  <Popover
                    open={openPopover === "structuredDataPrompt"}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? "structuredDataPrompt" : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 h-6 w-6 text-foreground hover:text-foreground hover:bg-secondary"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <Command>
                        <div className="p-2">
                          <Input
                            placeholder="Search micro prompts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {isLoadingPrompts ? (
                            <div className="flex justify-center p-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            </div>
                          ) : filteredMicroPrompts.length === 0 ? (
                            <p className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery
                                ? "No matching micro prompts found"
                                : "No micro prompts available"}
                            </p>
                          ) : (
                            filteredMicroPrompts.map((prompt) => (
                              <CommandItem
                                key={prompt.id}
                                onSelect={() =>
                                  insertMicroPrompt(
                                    "structuredDataPrompt",
                                    prompt.content,
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {prompt.title}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {prompt.content.length > 50
                                      ? prompt.content.substring(0, 50) + "..."
                                      : prompt.content}
                                  </span>
                                </div>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <FormDescription className="text-muted-foreground">
                Prompt used to extract structured data from the conversation.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="structuredJSONSchema"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">
                Structured JSON Schema
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Textarea
                    placeholder="{
  'type': 'object',
  'properties': {
    'name': { 'type': 'string' },
    'age': { 'type': 'number' }
  }
}"
                    className="min-h-32 bg-muted text-foreground border-input pr-10 font-mono"
                    {...field}
                    ref={textareaRefs.structuredJSONSchema}
                  />
                </FormControl>
                {microPrompts.length > 0 && (
                  <Popover
                    open={openPopover === "structuredJSONSchema"}
                    onOpenChange={(open) =>
                      setOpenPopover(open ? "structuredJSONSchema" : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 h-6 w-6 text-foreground hover:text-foreground hover:bg-secondary"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <Command>
                        <div className="p-2">
                          <Input
                            placeholder="Search micro prompts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {isLoadingPrompts ? (
                            <div className="flex justify-center p-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            </div>
                          ) : filteredMicroPrompts.length === 0 ? (
                            <p className="p-2 text-sm text-muted-foreground text-center">
                              {searchQuery
                                ? "No matching micro prompts found"
                                : "No micro prompts available"}
                            </p>
                          ) : (
                            filteredMicroPrompts.map((prompt) => (
                              <CommandItem
                                key={prompt.id}
                                onSelect={() =>
                                  insertMicroPrompt(
                                    "structuredJSONSchema",
                                    prompt.content,
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {prompt.title}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {prompt.content.length > 50
                                      ? prompt.content.substring(0, 50) + "..."
                                      : prompt.content}
                                  </span>
                                </div>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <FormDescription className="text-muted-foreground">
                JSON schema to validate structured data returned by the LLM.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
